/**
 * Fetches the *entire* set of pending adoption transfers (not just one page).
 *
 * The Devices page splits pending adoptions into two sections — recent
 * "Pending Transfers" and "Stalled Transfers" (older than N days) — and
 * paginates each client-side. That split can only be done accurately if we
 * hold the full list, so this hook drains every server page up front rather
 * than paging on demand like `useRescueDevices`. The page-drain bookkeeping is
 * shared with the Unassigned inventory section via `useDrainAllPages`.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import type { Device } from '../lib/types';
import type { RescueDevicesData } from '../lib/rescueQueries';
import { rescueDevicesQuery } from '../lib/rescueQueries';
import { mapRescueDeviceItemToDevice } from '../lib/deviceAdapters';
import useDrainAllPages from './useDrainAllPages';

/** Page size for the drain loop. The server clamps to its own max. */
const PENDING_PAGE_SIZE = 100;

export interface UseAllPendingAdoptionsResult {
  /** Every pending adoption transfer matching the current search. */
  devices: Device[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
}

export default function useAllPendingAdoptions(
  searchQuery: string,
  resellerName: string,
): UseAllPendingAdoptionsResult {
  const input = useMemo(() => {
    const base: Record<string, unknown> = { filter: 'PENDING_ADOPTIONS', limit: PENDING_PAGE_SIZE };
    if (searchQuery) {
      base.query = searchQuery;
    }
    return base;
  }, [searchQuery]);

  const { data, previousData, loading, error, fetchMore, refetch } = useQuery<RescueDevicesData>(rescueDevicesQuery, {
    variables: { input },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const hasMore = data?.rescueDevices?.hasMore ?? false;
  const itemsLength = data?.rescueDevices?.items.length ?? 0;
  const totalCount = data?.rescueDevices?.totalCount ?? 0;

  const mergeItems = useCallback(
    (previous: RescueDevicesData, fetchMoreResult: RescueDevicesData): RescueDevicesData => ({
      rescueDevices: {
        ...fetchMoreResult.rescueDevices,
        items: [...previous.rescueDevices.items, ...fetchMoreResult.rescueDevices.items],
      },
    }),
    [],
  );

  useDrainAllPages<RescueDevicesData>({ input, hasMore, itemsLength, totalCount, fetchMore, mergeItems });

  // Keep the last successful result visible while a new search is in flight
  // (matches the transition strategy in useRescueDevices).
  const effective = data ?? previousData;
  const devices: Device[] = useMemo(() => {
    const items = effective?.rescueDevices?.items;
    if (!items) {
      return [];
    }
    return items
      .map((item) => mapRescueDeviceItemToDevice(item, resellerName, 'PENDING_ADOPTIONS'))
      .filter((d): d is Device => d !== null);
  }, [effective?.rescueDevices?.items, resellerName]);

  return {
    devices,
    loading,
    error: error as Error | undefined,
    refetch: () => {
      void refetch();
    },
  };
}
